import "../styling/PaymentMethodsStyles.css";
import PaymentProcessingModal from "../../modal/PaymentProcessingModal";
import TransactionStatusModal from "../../modal/TransactionStatusModal";

// Clicking on the wrapper's padding (outside the iframe) doesn't naturally
// focus the Nuvei iframe. Forward the click to the iframe so users can tap
// anywhere in the field to start typing.
const focusNuveiIframe = (slotEl) => {
  const iframe = slotEl?.querySelector?.("iframe");
  if (iframe) {
    try { iframe.focus(); } catch (_) { /* cross-origin focus is best-effort */ }
  }
};

const handleWrapperMouseDown = (e) => {
  const slot = e.currentTarget.querySelector(".nuvei-field-slot");
  if (slot && !slot.contains(e.target)) {
    focusNuveiIframe(slot);
  }
};

/**
 * NuveiCardFormView
 *
 * Pure render component for the Nuvei three-part card fields.
 *
 * Nuvei Fields iframes (hosted, PCI-compliant):
 *   cardNumberRef  → ccNumber      (card number)
 *   cardExpiryRef  → ccExpiration  (expiry date)
 *   cardCvcRef     → ccCvc         (CVC/CVV)
 *
 * Per Nuvei docs, the element passed to `.attach()` must be a clean, empty
 * container so the SDK can inject its iframe unhindered. We therefore keep
 * the refs on dedicated inner slots and render the loading placeholder as
 * a *sibling*, never as a child of the slot itself.
 *
 * Cardholder name is a plain HTML input — Nuvei has no hosted ccHolder field.
 * It is passed as top-level `cardHolderName` to safecharge.createPayment().
 */
const NuveiCardFormView = ({
  t,
  loading,
  error,
  sdkLoading,
  cardReady,
  cardholderName,
  onCardholderNameChange,
  cardNumberRef,
  cardExpiryRef,
  cardCvcRef,
  showProcessingModal,
  setProcessingModal,
  showStatusModal,
  setShowStatusModal,
  transactionStatus,
  transactionId,
  hostedPageRedirect,
  checkoutConfDto,
}) => {
  // NOTE: we intentionally do NOT add `card-element-wrapper` here. The generic
  // rules on that class (built for dLocal) force `position: absolute` and a
  // fixed 44px height on any descendant `iframe` via `!important`, which
  // hijacks the Nuvei iframe and breaks focus/click delivery. Nuvei has its
  // own dedicated `nuvei-field-wrapper` styles.
  const wrapperClass = `nuvei-field-wrapper${!cardReady ? " loading" : ""}`;

  return (
    <div className="nuvei-card-container">
      {/* Cardholder Name — plain HTML input, not a Nuvei iframe */}
      <div className="form-section nuvei-field-section">
        <label className="input-label nuvei-field-label">
          {t("paymentForms.card.cardholderName") || "Cardholder Name"}
        </label>
        <input
          type="text"
          className="card-input nuvei-name-input"
          value={cardholderName}
          onChange={(e) => onCardholderNameChange(e.target.value)}
          placeholder={t("paymentForms.card.namePlaceholder") || "John Doe"}
          autoComplete="cc-name"
        />
      </div>

      {/* Card Number — Nuvei ccNumber iframe */}
      <div className="form-section nuvei-field-section">
        <label className="input-label nuvei-field-label">
          {t("paymentForms.card.cardNumber") || "Card Number"}
        </label>
        <div className={wrapperClass} onMouseDown={handleWrapperMouseDown}>
          <div
            ref={cardNumberRef}
            className="nuvei-field-slot"
            data-nuvei-field="ccNumber"
          />
          {sdkLoading && !cardReady && (
            <span className="sdk-loading-text">
              {t("paymentForms.card.loadingSDK") || "Loading…"}
            </span>
          )}
        </div>
      </div>

      {/* Expiry + CVC row — Nuvei ccExpiration and ccCvc iframes */}
      <div className="nuvei-expiry-cvc-row">
        <div className="form-section nuvei-field-section nuvei-field-half">
          <label className="input-label nuvei-field-label">
            {t("paymentForms.card.expiryDate") || "Expiry Date"}
          </label>
          <div className={wrapperClass} onMouseDown={handleWrapperMouseDown}>
            <div
              ref={cardExpiryRef}
              className="nuvei-field-slot"
              data-nuvei-field="ccExpiration"
            />
            {sdkLoading && !cardReady && (
              <span className="sdk-loading-text">MM / YY</span>
            )}
          </div>
        </div>

        <div className="form-section nuvei-field-section nuvei-field-half">
          <label className="input-label nuvei-field-label">
            {t("paymentForms.card.cvv") || "CVV"}
          </label>
          <div className={wrapperClass} onMouseDown={handleWrapperMouseDown}>
            <div
              ref={cardCvcRef}
              className="nuvei-field-slot"
              data-nuvei-field="ccCvc"
            />
            {sdkLoading && !cardReady && (
              <span className="sdk-loading-text">CVV</span>
            )}
          </div>
        </div>
      </div>

      {/* Error display */}
      {error && <div className="error-message">{error}</div>}

      {/* Loading overlay while processing payment */}
      {loading && (
        <div className="loader-overlay">
          <div className="spinner-large" />
          <div className="loading-text">
            {t("paymentForms.card.processing") || "Processing…"}
          </div>
        </div>
      )}

      <PaymentProcessingModal
        show={showProcessingModal}
        setShow={setProcessingModal}
      />

      <TransactionStatusModal
        show={showStatusModal}
        setShow={setShowStatusModal}
        status={transactionStatus}
        transactionId={transactionId}
        hostedPageRedirect={hostedPageRedirect}
        fallbackRedirectUrl={checkoutConfDto?.checkoutRedirectUrl}
      />
    </div>
  );
};

export default NuveiCardFormView;
